/**
 * trend.ts — linear-regression drift detector for test-kit series data.
 *
 * No existing monitor drift function was found in apps/web/src/monitor/;
 * this implements linear regression directly as the canonical implementation.
 * The monitor can import from here if it ever needs drift detection.
 */

export interface TrendResult {
  /** Overall direction over the window. */
  direction: 'up' | 'down' | 'flat';
  /** Slope in (parameter units) per calendar day. Negative = falling. */
  perDayRate: number;
}

const MS_PER_DAY = 86_400_000;

/**
 * Compute a linear-regression trend over the most recent `windowDays` of
 * the series. Returns `flat` when there are fewer than 2 points or when
 * the drift is small relative to the parameter's magnitude.
 *
 * @param series  Newest-first OR oldest-first array of `{ ts, value }` points.
 * @param windowDays  Only points within this many days of the newest point
 *                    are used (default 7).
 */
export function computeTrend(
  series: ReadonlyArray<{ ts: string; value: number }>,
  windowDays = 7,
): TrendResult {
  if (series.length < 2) {
    return { direction: 'flat', perDayRate: 0 };
  }

  // Sort ascending by timestamp for regression.
  const sorted = [...series].sort(
    (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
  );

  // Filter to the window around the newest point.
  const newest = sorted[sorted.length - 1];
  if (!newest) return { direction: 'flat', perDayRate: 0 };
  const newestMs = new Date(newest.ts).getTime();
  const windowMs = windowDays * MS_PER_DAY;
  const windowed = sorted.filter(
    (p) => newestMs - new Date(p.ts).getTime() <= windowMs,
  );

  if (windowed.length < 2) {
    return { direction: 'flat', perDayRate: 0 };
  }

  // Normalise x to days relative to the first point in the window
  // (avoids large-number precision loss in the regression).
  const first = windowed[0];
  if (!first) return { direction: 'flat', perDayRate: 0 };
  const originMs = new Date(first.ts).getTime();

  const pts = windowed.map((p) => ({
    x: (new Date(p.ts).getTime() - originMs) / MS_PER_DAY,
    y: p.value,
  }));

  const n = pts.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  for (const { x, y } of pts) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }

  const denom = n * sumXX - sumX * sumX;
  const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;

  // "Flat" = slope magnitude is less than 0.5% of the mean absolute value per day.
  const meanValue = sumY / n;
  const flatThreshold = Math.max(0.0001, Math.abs(meanValue) * 0.005);

  const direction: TrendResult['direction'] =
    Math.abs(slope) < flatThreshold ? 'flat' : slope > 0 ? 'up' : 'down';

  return { direction, perDayRate: slope };
}
